import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
// ドラッグ機能を削除
import { StepNavigation } from '../StepNavigation/StepNavigation';
// DraggableProductCard を削除
import { ProductDetailModal } from '../ProductDetailModal/ProductDetailModal';
import { PreviewModal } from '../PreviewModal/PreviewModal';
import { CompactPreview } from './RubricGeneration/CompactPreview';
import { CompactEdit } from './RubricGeneration/CompactEdit';
import { useEvaluationStore } from '../../store/evaluationStore';
import '../../styles/components/quick-evaluation.css';

export const ComparativeEvaluationQuick: React.FC = () => {
  const { t } = useTranslation();
  const { 
    products, 
    criteria, 
    setCurrentStep, 
    setEvaluationPosition, 
    getEvaluationPosition,
    getRubricData,
    getGrades,
    saveDataAsync 
  } = useEvaluationStore();

  const [currentCriterionIndex, setCurrentCriterionIndex] = useState(0);
  // 完了状態を削除
  const [previewPosition, setPreviewPosition] = useState<number | null>(null);
  const [previewGrade, setPreviewGrade] = useState<string>('');
  const [previewDescription, setPreviewDescription] = useState<string>('');
  
  // マウス移動時のプレビュー
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.min(100, Math.max(0, (x / rect.width) * 100));
    
    setPreviewPosition(percentage);
    const gradeInfo = getGradeFromPosition(percentage);
    setPreviewGrade(gradeInfo.grade);
    setPreviewDescription(gradeInfo.description);
  };
  // 観点完了画面を削除
  // ドラッグ関連の状態を削除

  const [detailModal, setDetailModal] = useState<{ isOpen: boolean; productIndex: number }>({
    isOpen: false,
    productIndex: 0
  });
  
  const [previewModal, setPreviewModal] = useState<{ isOpen: boolean; url: string; title: string }>({
    isOpen: false,
    url: '',
    title: ''
  });

  // サイドパネル用の状態
  const [currentHoverProductIndex, setCurrentHoverProductIndex] = useState<number | null>(null);
  const [editingProductIndex, setEditingProductIndex] = useState<number | null>(null);

  // 現在の観点での順位を計算する関数
  const calculateRankForCriterion = (productIndex: number, criterionIndex: number): number | undefined => {
    const placedProducts = products
      .map((_, index) => ({
        index,
        position: getEvaluationPosition(index, criterionIndex)
      }))
      .filter(item => item.position !== null)
      .sort((a, b) => (b.position?.x || 0) - (a.position?.x || 0)); // 高い順でソート

    const rank = placedProducts.findIndex(item => item.index === productIndex);
    return rank >= 0 ? rank + 1 : undefined;
  };
  
  // 現在の観点で未配置の製品を取得
  const getUnplacedProducts = (criterionIndex: number) => {
    return products.filter((_, productIndex) => 
      !getEvaluationPosition(productIndex, criterionIndex)
    );
  };

  // クイック評価ではすべての製品を表示
  const currentCriterion = criteria[currentCriterionIndex];
  
  // クイック評価モードでは全製品の評価が必要
  const requiredComparisons = products.length;
  
  // 各観点での配置済み数を計算
  const getPlacedCountForCriterion = (criterionIndex: number) => {
    return products.filter((_, productIndex) =>
      getEvaluationPosition(productIndex, criterionIndex) !== null
    ).length;
  };
  const rubricData = getRubricData();
  const gradeSettings = getGrades(currentCriterionIndex);
  
  // EvaluationLineのrefを追加
  const lineRef = React.useRef<HTMLDivElement>(null);
  
  // ドラッグ関連の機能をすべて削除

  // デバッグ用
  console.log('ルーブリックデータ:', rubricData);
  console.log('現在の観点インデックス:', currentCriterionIndex);
  console.log('現在の観点のルーブリック:', rubricData?.[currentCriterionIndex]);

  // 自動進行機能を削除

  // 位置をグレードに変換
  const getGradeFromPosition = (position: number): { grade: string; description: string } => {
    const percentage = position;
    const criterionRubric = rubricData?.[currentCriterionIndex];
    
    // 実際の等級設定を使用
    if (gradeSettings && gradeSettings.length > 0) {
      // 等級設定を降順でソート（高い境界値から低い境界値へ）
      const sortedGrades = gradeSettings.slice().sort((a, b) => b.gradeXStart - a.gradeXStart);
      
      for (const grade of sortedGrades) {
        if (percentage >= grade.gradeXStart) {
          const rubricCell = criterionRubric?.find(cell => cell.grade === grade.gradeLetter);
          return { 
            grade: grade.gradeLetter, 
            description: rubricCell?.description || `${grade.gradeLetter}等級` 
          };
        }
      }
      
      // どの境界値よりも低い場合は最低等級
      const lowestGrade = sortedGrades[sortedGrades.length - 1];
      const rubricCell = criterionRubric?.find(cell => cell.grade === lowestGrade.gradeLetter);
      return { 
        grade: lowestGrade.gradeLetter, 
        description: rubricCell?.description || `${lowestGrade.gradeLetter}等級` 
      };
    }
    
    // フォールバック（等級設定がない場合）
    if (!criterionRubric) {
      if (percentage >= 90) return { grade: 'A', description: t('comparativeEvaluationQuick.grades.excellent') };
      else if (percentage >= 70) return { grade: 'B', description: t('comparativeEvaluationQuick.grades.good') };
      else if (percentage >= 50) return { grade: 'C', description: t('comparativeEvaluationQuick.grades.average') };
      else return { grade: 'D', description: t('comparativeEvaluationQuick.grades.needsImprovement') };
    }

    // ルーブリックデータがある場合のフォールバック
    if (percentage >= 90) {
      const aGrade = criterionRubric.find(cell => cell.grade === 'A');
      return { grade: 'A', description: aGrade?.description || t('comparativeEvaluationQuick.grades.excellent') };
    } else if (percentage >= 70) {
      const bGrade = criterionRubric.find(cell => cell.grade === 'B');
      return { grade: 'B', description: bGrade?.description || t('comparativeEvaluationQuick.grades.good') };
    } else if (percentage >= 50) {
      const cGrade = criterionRubric.find(cell => cell.grade === 'C');
      return { grade: 'C', description: cGrade?.description || t('comparativeEvaluationQuick.grades.average') };
    } else {
      const dGrade = criterionRubric.find(cell => cell.grade === 'D');
      return { grade: 'D', description: dGrade?.description || t('comparativeEvaluationQuick.grades.needsImprovement') };
    }
  };

  // 重複回避のための位置計算関数
  const calculatePositionWithOverlapAvoidance = (targetX: number, excludeProductIndex: number): { x: number; y: number } => {
    const placedPositions = products
      .map((_, index) => {
        if (index === excludeProductIndex) return null; // 現在配置中のアイテムは除外
        return getEvaluationPosition(index, currentCriterionIndex);
      })
      .filter((pos): pos is { x: number; y: number } => pos !== null);

    let adjustedY = 0;
    const threshold = 15; // 重複判定の閾値を15%に設定

    // 同じようなx座標に配置されているアイテムを見つける
    const nearbyItems = placedPositions.filter(pos => 
      Math.abs(pos.x - targetX) < threshold
    );

    if (nearbyItems.length > 0) {
      // y座標を調整して重複を避ける
      const usedYPositions = nearbyItems.map(pos => pos.y).sort((a, b) => a - b);
      
      // 既存のY位置から十分離れた位置を見つける
      let candidateY = 0;
      while (true) {
        const isTooClose = usedYPositions.some(usedY => Math.abs(candidateY - usedY) < 3);
        if (!isTooClose) {
          adjustedY = candidateY;
          break;
        }
        candidateY += 3; // Y座標の間隔を3単位に設定
        
        // 安全策として無限ループを防ぐ
        if (candidateY > 50) {
          adjustedY = candidateY;
          break;
        }
      }
    }

    return { x: targetX, y: adjustedY };
  };

  // クイック評価用のマウス移動ハンドラ
  const handleQuickEvalMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.min(100, Math.max(0, (x / rect.width) * 100));
    
    setPreviewPosition(percentage);
    const gradeInfo = getGradeFromPosition(percentage);
    setPreviewGrade(gradeInfo.grade);
    setPreviewDescription(gradeInfo.description);
  };

  // ワンクリック評価: 配置領域をクリックして未配置製品を自動配置
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // 未配置製品を取得
    const unplacedProducts = getUnplacedProducts(currentCriterionIndex);
    if (unplacedProducts.length === 0) return;
    
    // 最初の未配置製品を選択
    const productToPlace = products.findIndex((_, index) => 
      !getEvaluationPosition(index, currentCriterionIndex)
    );
    
    if (productToPlace === -1) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.min(100, Math.max(0, (x / rect.width) * 100));
    
    console.log('Click position:', { x: e.clientX, rect: rect.left, width: rect.width, percentage });
    
    // 重複回避の位置計算
    const position = calculatePositionWithOverlapAvoidance(percentage, productToPlace);
    
    console.log('Calculated position:', position);
    
    // 位置を設定
    setEvaluationPosition(productToPlace, currentCriterionIndex, position.x, position.y);
  };

  const handleMouseLeave = () => {
    setPreviewPosition(null);
    setPreviewGrade('');
    setPreviewDescription('');
  };
  
  const handleNext = () => {
    setCurrentStep(7);
  };

  const handlePrev = async () => {
    await saveDataAsync();
    setCurrentStep(5);
  };

  const handleSkipToAll = () => {
    setCurrentStep(4); // 全配置モードに戻る
  };

  const handleNextStep = () => {
    setCurrentStep(7); // レポート生成へ
  };

  const openDetailModal = (productIndex: number) => {
    const product = products[productIndex];
    if (product && product.reviewLink) {
      // プレビューリンクがある場合は直接プレビューを開く
      openPreview(product.reviewLink, product.reviewTitle);
    } else {
      // プレビューリンクがない場合は編集モーダルを開く
      setEditingProductIndex(productIndex);
    }
  };

  const closeDetailModal = () => {
    setDetailModal({
      isOpen: false,
      productIndex: 0
    });
  };

  const openPreview = (url: string, title: string) => {
    setPreviewModal({
      isOpen: true,
      url,
      title
    });
  };

  const closePreview = () => {
    setPreviewModal({
      isOpen: false,
      url: '',
      title: ''
    });
  };

  // 観点切替機能を簡素化
  const handleCriterionChange = (criterionIndex: number) => {
    setCurrentCriterionIndex(criterionIndex);
  };

  // 観点ナビゲーション関連を削除

  // キーボードショートカットを削除

  // 完了画面を削除

  // 観点完了画面を削除

  if (!currentCriterion || products.length === 0) {
    return (
      <section className="section">
        <div className="empty-state">
          <h2 className="empty-state__title">{t('comparativeEvaluationQuick.empty.title')}</h2>
          <p className="empty-state__description">{t('comparativeEvaluationQuick.empty.description')}</p>
        </div>
      </section>
    );
  }

  // 進行状況と次の行動を決定
  const placedCountForCurrentCriterion = getPlacedCountForCriterion(currentCriterionIndex);
  const isCurrentCriterionComplete = placedCountForCurrentCriterion >= requiredComparisons;
  
  const allCriteriaComplete = criteria.every((_, index) => 
    getPlacedCountForCriterion(index) >= requiredComparisons
  );

  // カーソルテキストを決定 - 削除
  // let cursorText = '';
  // if (allCriteriaComplete) {
  //   cursorText = t('comparativeEvaluationQuick.cursor.nextStep');
  // } else if (isCurrentCriterionComplete) {
  //   cursorText = t('comparativeEvaluationQuick.cursor.nextCriterion');
  // }

  return (
    <>
      <div className="quick-evaluation-container">
        <section className="section quick-evaluation-main">
          <div className="section__header">
            <h2 className="section__title">{t('comparativeEvaluationQuick.title')}</h2>
            {/* カーソルテキスト表示 - 削除 */}
          </div>

          <div className="comparison-evaluation">
          {/* 評価エリア - ドラッグ機能を削除 */}
          <div className="evaluation-area">
            {/* 数直線 */}
            <div className="evaluation-line-container evaluation-line-quick">
              <div className="evaluation-line-labels">
                <span className="evaluation-line-label evaluation-line-label--start">{t('comparisonEvaluation.evaluationLine.low')}</span>
                <span className="evaluation-line-label evaluation-line-label--end">{t('comparisonEvaluation.evaluationLine.high')}</span>
              </div>
            {/* 通常のEvaluationLineを非表示にして、クイック評価専用の配置領域を作成 */}
            <div
              className="evaluation-line-quick"
              ref={lineRef}
              onMouseMove={handleQuickEvalMouseMove}
              onMouseLeave={handleMouseLeave}
              onClick={handleClick}
              style={{
                position: 'relative',
                height: '800px',
                width: '100%',
                background: 'transparent',
                border: '3px solid var(--color-primary)',
                borderRadius: '0.75rem',
                cursor: 'crosshair',
                padding: '2rem 1.5rem 4rem',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              {/* グレード背景 */}
              <div className="grade-background" style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '800px', display: 'flex', zIndex: 1 }}>
                {gradeSettings && gradeSettings.length > 0 ? (
                  // 実際の等級設定に基づいて表示
                  (() => {
                    const sortedGrades = gradeSettings.slice().sort((a, b) => b.gradeXStart - a.gradeXStart);
                    const sections = [];

                    sortedGrades.forEach((grade, index) => {
                      const start = grade.gradeXStart;
                      const end = index > 0 ? sortedGrades[index - 1].gradeXStart : 100;
                      const width = end - start;

                      sections.push(
                        <div
                          key={grade.gradeLetter}
                          className={`grade-section grade-${grade.gradeLetter.toLowerCase()}`}
                          style={{
                            position: 'absolute',
                            left: `${start}%`,
                            width: `${width}%`,
                            top: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            flexDirection: 'column',
                            padding: '2rem',
                            opacity: 0.7,
                            borderRight: '2px solid rgba(255,255,255,0.3)',
                            transition: 'opacity 0.2s ease',
                          }}
                        >
                          <div className="grade-label" style={{ fontSize: '4rem', fontWeight: 'bold', color: 'white', marginBottom: '1rem' }}>
                            {grade.gradeLetter}
                          </div>
                          <div className="grade-description" style={{ fontSize: '1rem', color: 'white', textAlign: 'center', lineHeight: '1.5', maxWidth: '90%' }}>
                            {rubricData?.[currentCriterionIndex]?.find(cell => cell.grade === grade.gradeLetter)?.description || ''}
                          </div>
                        </div>
                      );
                    });

                    // 最低等級の前の部分
                    if (sortedGrades.length > 0) {
                      const lowestGrade = sortedGrades[sortedGrades.length - 1];
                      if (lowestGrade.gradeXStart > 0) {
                        sections.push(
                          <div
                            key={`${lowestGrade.gradeLetter}-below`}
                            className={`grade-section grade-${lowestGrade.gradeLetter.toLowerCase()}`}
                            style={{
                              position: 'absolute',
                              left: '0%',
                              width: `${lowestGrade.gradeXStart}%`,
                              top: 0,
                              bottom: 0,
                              display: 'flex',
                              alignItems: 'flex-start',
                              justifyContent: 'center',
                              flexDirection: 'column',
                              padding: '2rem',
                              opacity: 0.7,
                            }}
                          >
                            <div className="grade-label" style={{ fontSize: '4rem', fontWeight: 'bold', color: 'white', marginBottom: '1rem' }}>
                              {lowestGrade.gradeLetter}
                            </div>
                            <div className="grade-description" style={{ fontSize: '1rem', color: 'white', textAlign: 'center', lineHeight: '1.5', maxWidth: '90%' }}>
                              {rubricData?.[currentCriterionIndex]?.find(cell => cell.grade === lowestGrade.gradeLetter)?.description || ''}
                            </div>
                          </div>
                        );
                      }
                    }

                    return sections;
                  })()
                ) : (
                  // フォールバック
                  <>
                    <div className="grade-section grade-d" style={{ position: 'absolute', left: '0%', width: '50%', top: 0, bottom: 0, background: 'linear-gradient(135deg, var(--color-grade-d), #dc2626)', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', paddingTop: '2rem', paddingLeft: '2rem', opacity: 0.7 }}>
                      <div style={{ fontSize: '4rem', fontWeight: 'bold', color: 'white' }}>D</div>
                      <div style={{ fontSize: '1rem', color: 'white', paddingRight: '2rem', textAlign: 'left', lineHeight: '1.5', marginTop: '1rem' }}>{t('comparativeEvaluationQuick.grades.needsImprovement')}</div>
                    </div>
                    <div className="grade-section grade-c" style={{ position: 'absolute', left: '50%', width: '20%', top: 0, bottom: 0, background: 'linear-gradient(135deg, var(--color-grade-c), #d97706)', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', paddingTop: '2rem', paddingLeft: '2rem', opacity: 0.7 }}>
                      <div style={{ fontSize: '4rem', fontWeight: 'bold', color: 'white' }}>C</div>
                      <div style={{ fontSize: '1rem', color: 'white', paddingRight: '2rem', textAlign: 'left', lineHeight: '1.5', marginTop: '1rem' }}>{t('comparativeEvaluationQuick.grades.average')}</div>
                    </div>
                    <div className="grade-section grade-b" style={{ position: 'absolute', left: '70%', width: '20%', top: 0, bottom: 0, background: 'linear-gradient(135deg, var(--color-grade-b), #2563eb)', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', paddingTop: '2rem', paddingLeft: '2rem', opacity: 0.7 }}>
                      <div style={{ fontSize: '4rem', fontWeight: 'bold', color: 'white' }}>B</div>
                      <div style={{ fontSize: '1rem', color: 'white', paddingRight: '2rem', textAlign: 'left', lineHeight: '1.5', marginTop: '1rem' }}>{t('comparativeEvaluationQuick.grades.good')}</div>
                    </div>
                    <div className="grade-section grade-a" style={{ position: 'absolute', left: '90%', width: '10%', top: 0, bottom: 0, background: 'linear-gradient(135deg, var(--color-grade-a), #059669)', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', paddingTop: '2rem', paddingLeft: '1rem', opacity: 0.7 }}>
                      <div style={{ fontSize: '4rem', fontWeight: 'bold', color: 'white' }}>A</div>
                      <div style={{ fontSize: '0.875rem', color: 'white', paddingRight: '0.5rem', textAlign: 'left', lineHeight: '1.4', marginTop: '1rem' }}>{t('comparativeEvaluationQuick.grades.excellent')}</div>
                    </div>
                  </>
                )}
              </div>

              {/* 既に配置された製品 */}
              {products.map((product, index) => {
                const position = getEvaluationPosition(index, currentCriterionIndex);
                if (position) {
                  return (
                    <div
                      key={index}
                      className="placed-item"
                      style={{
                        position: 'absolute',
                        left: `${position.x}%`,
                        top: `${50 + position.y * 1.5}%`, // Y座標を上方に調整
                        transform: 'translate(-50%, -50%)',
                        background: 'var(--color-background)',
                        border: '2px solid var(--color-primary)',
                        borderRadius: '8px',
                        padding: '0.5rem 0.75rem',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        zIndex: 10,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      }}
                      onDoubleClick={() => openDetailModal(index)}
                      title={product.reviewTitle}
                    >
                      {product.reviewTitle}
                    </div>
                  );
                }
                return null;
              })}

              {/* プレビュー: 次に配置される製品と評価位置を表示 */}
              {previewPosition !== null && getUnplacedProducts(currentCriterionIndex).length > 0 && (
                <div
                  className="placement-preview"
                  style={{
                    position: 'absolute',
                    left: `${previewPosition}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    background: 'var(--color-primary)',
                    color: 'white',
                    border: '2px solid var(--color-primary-dark)',
                    borderRadius: '8px',
                    padding: '0.75rem 1rem',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    zIndex: 15,
                    boxShadow: '0 6px 12px rgba(59, 130, 246, 0.3)',
                    textAlign: 'center',
                    userSelect: 'none',
                    pointerEvents: 'none',
                    animation: 'pulse 1s infinite',
                  }}
                >
                  <div>
                    {getUnplacedProducts(currentCriterionIndex)[0]?.reviewTitle}
                  </div>
                </div>
              )}
            </div>

            <div className="evaluation-line-scale">
              {[0, 25, 50, 75, 100].map(value => (
                <div key={value} className="scale-mark" style={{ left: `${value}%` }}>
                  <div className="scale-mark__line" />
                  <div className="scale-mark__label">{value}</div>
                </div>
              ))}
            </div>

          </div>

            {/* 観点選択 - 配置領域外 */}
            <div className="criterion-selector" style={{ marginTop: '1rem' }}>
              <span className="criterion-selector__label">{t('comparativeEvaluationQuick.criterionSelector.title')}</span>
              <div className="criterion-tabs">
                {criteria.map((criterion, index) => {
                  const placedCount = getPlacedCountForCriterion(index);
                  const isSufficient = placedCount >= requiredComparisons;
                  const progressPercentage = requiredComparisons > 0 
                    ? Math.min((placedCount / requiredComparisons) * 100, 100)
                    : 0;

                  return (
                    <button
                      key={index}
                      className={`criterion-tab ${index === currentCriterionIndex ? 'active' : ''} ${isSufficient ? 'sufficient' : ''}`}
                      onClick={() => setCurrentCriterionIndex(index)}
                    >
                      <span className="criterion-tab__title">{criterion.title}</span>
                      <div className="criterion-tab__progress-bar">
                        <div 
                          className="criterion-tab__progress-fill" 
                          style={{ width: `${progressPercentage}%` }}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

          {/* 評価対象リスト - 横並び表示（ドラッグ無効） */}
          <div className="products-panel products-panel--horizontal">
            <h4 className="products-panel__title">
              {t('comparativeEvaluationQuick.productsPanel.title')}
            </h4>
            <div className="product-items product-items--horizontal">
              {products.map((product, index) => {
                const position = getEvaluationPosition(index, currentCriterionIndex);
                const isPlaced = position !== null;

                return (
                  <div
                    key={index}
                    className={`product-card ${isPlaced ? 'product-card--placed' : ''} ${currentHoverProductIndex === index ? 'product-card--hovering' : ''}`}
                    onDoubleClick={() => openDetailModal(index)}
                    onMouseEnter={() => !isPlaced && setCurrentHoverProductIndex(index)}
                    onMouseLeave={() => setCurrentHoverProductIndex(null)}
                    style={{
                      cursor: 'pointer',
                      opacity: isPlaced ? 0.6 : 1,
                    }}
                  >
                    <div className="product-card__title">
                      {product.reviewTitle}
                    </div>
                    {product.reviewComment && (
                      <div className="product-card__detail">
                        <p className="product-card__detail-text">{product.reviewComment}</p>
                      </div>
                    )}
                    {isPlaced && (
                      <div className="product-card__badge" style={{ background: 'var(--color-success)', color: 'white', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>
                        ✓ 評価済み
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          </div>
        </div>
        </section>
          
        {/* サイドパネル */}
        {currentHoverProductIndex !== null && (
          <div className="quick-evaluation-side">
            <div className="side-panel-header">
              <span className="side-panel-label">配置対象</span>
            </div>
            {products[currentHoverProductIndex]?.reviewLink ? (
              <CompactPreview
                url={products[currentHoverProductIndex].reviewLink}
                title={products[currentHoverProductIndex].reviewTitle}
                comment={products[currentHoverProductIndex].reviewComment}
              />
            ) : (
              <CompactEdit productIndex={currentHoverProductIndex} />
            )}
          </div>
        )}
      </div>
      
      <StepNavigation
        currentStep={6}
        canProceed={true}
        onNext={async () => {
          await saveDataAsync();
          setCurrentStep(7);
        }}
        onPrev={handlePrev}
        nextLabel={t('comparativeEvaluationQuick.nextButton')}
        prevLabel={t('comparativeEvaluationQuick.prevButton')}
      />

      {/* 詳細表示モーダル */}
      <ProductDetailModal
        isOpen={detailModal.isOpen}
        productIndex={detailModal.productIndex}
        onClose={closeDetailModal}
        onPreview={openPreview}
      />

      {/* プレビュー用モーダル */}
      <PreviewModal 
        isOpen={previewModal.isOpen}
        url={previewModal.url}
        title={previewModal.title}
        onClose={closePreview}
      />
    </>
  );
};
